import HeaderPaneProps from './HeaderPaneProps';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { Panel, PanelType } from 'office-ui-fabric-react/lib/Panel';
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getActiveCharm, HeaderCharmType } from 'owa-header-store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import strings from 'owa-strings/owa-common-strings';
import { getHostLocation } from 'owa-url/lib/hostLocation';
import * as React from 'react';
import {
    getMailPath,
    getCalendarPath,
    getPeoplePath,
    getFilesHubPath,
    getFilesHubImageTabPath,
    getOwaUrlWithAddedQueryParameters,
} from 'owa-url';

const styles = require('./WafflePane.scss');
const classNames = require('classnames');

const indexedReactDefaultPage = getMailPath() + 'default.aspx';
const indexedCalendarDefaultPage = getCalendarPath() + 'default.aspx';
const indexedPeopleDefaultPage = getPeoplePath() + 'default.aspx';

interface AppTile {
    displayName: string;
    icon: string;
    href: string;
    hoverBackground: string;
}

function createAppTile(
    displayName: string,
    icon: string,
    consumerHref: string,
    enterpriseHref: string,
    hoverBackground: string
): AppTile {
    return {
        displayName: displayName,
        icon: icon,
        href: isFeatureEnabled('fwk-consumerWaffle') ? consumerHref : enterpriseHref,
        hoverBackground: hoverBackground,
    };
}

function isOwaMailReactLocation() {
    const lowercasePathname = getHostLocation().pathname.toLowerCase();
    if (lowercasePathname === getMailPath() || lowercasePathname === indexedReactDefaultPage) {
        return true;
    }

    return false;
}

function isOwaCalendarReactLocation() {
    const lowercasePathname = getHostLocation().pathname.toLowerCase();
    if (
        lowercasePathname === getCalendarPath() ||
        lowercasePathname === indexedCalendarDefaultPage
    ) {
        return true;
    }

    return false;
}

function isOwaPeopleReactLocation() {
    const lowercasePathname = getHostLocation().pathname.toLowerCase();
    if (lowercasePathname === getPeoplePath() || lowercasePathname === indexedPeopleDefaultPage) {
        return true;
    }

    return false;
}

function isOwaFilesHubReactLocation() {
    const lowercasePathname = getHostLocation().pathname.toLowerCase();
    if (lowercasePathname === getFilesHubPath()) {
        return true;
    }

    return false;
}

function getMailAppHref() {
    return isOwaMailReactLocation() ? '' : getMailPath();
}

function getFilesHubAppImagesTabHref() {
    return isOwaFilesHubReactLocation() ? '' : getFilesHubImageTabPath();
}

function getCalendarAppHref() {
    return isOwaCalendarReactLocation() ? '' : getCalendarPath();
}

function getPeopleAppHref() {
    return isOwaPeopleReactLocation() ? '' : getPeoplePath();
}

function createPhotoTile(): AppTile {
    const appHRef = getFilesHubAppImagesTabHref();
    return createAppTile(strings.appPhotos, ControlIcons.Photo2, appHRef, appHRef, styles.appPhoto);
}

function isCalendarFlightSettingEnabled() {
    const userOptions = getUserConfiguration().UserOptions;
    if (userOptions && userOptions.ReactOptinSettings) {
        return userOptions.ReactOptinSettings.CalendarEnabled;
    } else {
        return false;
    }
}

function isPeopleFlightSettingEnabled() {
    const userOptions = getUserConfiguration().UserOptions;
    if (userOptions && userOptions.ReactOptinSettings) {
        return userOptions.ReactOptinSettings.PeopleEnabled;
    } else {
        return false;
    }
}

const Apps = computed(() =>
    [
        createAppTile(
            strings.appMail,
            ControlIcons.OutlookLogo,
            getMailAppHref(),
            getMailAppHref(),
            styles.appMail
        ),
        createAppTile(
            strings.appCalendar,
            ControlIcons.Calendar,
            isFeatureEnabled('cal-react') || isCalendarFlightSettingEnabled()
                ? getCalendarAppHref()
                : getOwaUrlWithAddedQueryParameters({ path: '/calendar' }),
            isFeatureEnabled('cal-react') || isCalendarFlightSettingEnabled()
                ? getCalendarAppHref()
                : getOwaUrlWithAddedQueryParameters({ path: '/calendar' }),
            styles.appCalendar
        ),
        createAppTile(
            strings.appPeople,
            ControlIcons.People,
            isFeatureEnabled('fwk-peopleReact') || isPeopleFlightSettingEnabled()
                ? getPeopleAppHref()
                : getOwaUrlWithAddedQueryParameters({ path: '/people' }),
            isFeatureEnabled('fwk-peopleReact') || isPeopleFlightSettingEnabled()
                ? getPeopleAppHref()
                : getOwaUrlWithAddedQueryParameters({ path: '/people' }),
            styles.appPeople
        ),
    ]
        .concat(isFeatureEnabled('doc-fileshub') && isConsumer() ? [createPhotoTile()] : [])
        .concat([
            createAppTile(
                strings.appOneDrive,
                ControlIcons.OneDriveLogo,
                'https://onedrive.live.com',
                'https://microsoft-my.sharepoint.com/_layouts/15/MySite.aspx?MySiteRedirect=AllDocuments',
                styles.appOneDrive
            ),
            createAppTile(
                strings.appTasks,
                ControlIcons.CheckboxComposite,
                getOwaUrlWithAddedQueryParameters({ path: '/tasks' }),
                getOwaUrlWithAddedQueryParameters({ path: '/tasks' }),
                styles.appTasks
            ),
            createAppTile(
                strings.appWord,
                ControlIcons.WordLogo,
                'https://office.live.com/start/Word.aspx?auth=1',
                'https://office.live.com/start/Word.aspx?auth=2',
                styles.appWord
            ),
            createAppTile(
                strings.appExcel,
                ControlIcons.ExcelLogo,
                'https://office.live.com/start/Excel.aspx?auth=1',
                'https://office.live.com/start/Excel.aspx?auth=2',
                styles.appExcel
            ),
            createAppTile(
                strings.appPowerPoint,
                ControlIcons.PowerPointLogo,
                'https://office.live.com/start/PowerPoint.aspx?auth=1',
                'https://office.live.com/start/PowerPoint.aspx?auth=2',
                styles.appPowerPoint
            ),
            createAppTile(
                strings.appOneNote,
                ControlIcons.OneNoteLogo,
                'https://www.onenote.com/notebooks?auth=1',
                'https://www.onenote.com/notebooks?auth=2',
                styles.appOneNote
            ),
            createAppTile(
                strings.appSway,
                ControlIcons.SwayLogoInverse,
                'https://sway.com',
                'https://sway.com',
                styles.appSway
            ),
        ])
        .concat(
            isFeatureEnabled('fwk-consumerWaffle')
                ? [
                      createAppTile(
                          strings.appSkype,
                          ControlIcons.SkypeLogo,
                          'https://web.skype.com/?source=owa',
                          '',
                          styles.appSkype
                      ),
                      createAppTile(
                          strings.appOffice,
                          ControlIcons.OfficeLogo,
                          'https://office.live.com/start/default.aspx?auth=1&nf=1',
                          '',
                          styles.appOffice
                      ),
                      createAppTile(
                          strings.appBing,
                          ControlIcons.BingLogo,
                          'https://www.bing.com/?FORM=MO0027',
                          '',
                          styles.appBing
                      ),
                      createAppTile(
                          strings.appMSN,
                          ControlIcons.MsnLogo,
                          'https://www.msn.com/?OCID=CALHeader',
                          '',
                          styles.appMSN
                      ),
                      createAppTile(
                          strings.appFlow,
                          ControlIcons.Flow,
                          'https://flow.microsoft.com/manage/?utm_source=consumer_office&utm_medium=app_launcher&utm_campaign=consumer_referrals',
                          '',
                          styles.appFlow
                      ),
                      createAppTile(
                          strings.appForms,
                          ControlIcons.OfficeFormsLogoInverse24,
                          `https://forms.office.com/Pages/DesignPage.aspx?auth_pvr=WindowsLiveId&auth_upn=${getUserConfiguration()
                              .SessionSettings.UserPrincipalName || ''}&origin=shell`,
                          '',
                          styles.appForms
                      ),
                  ]
                : []
        )
        .concat(
            isFeatureEnabled('fwk-bookingsModule')
                ? [
                      createAppTile(
                          strings.appBookings,
                          ControlIcons.BookingsLogo,
                          getOwaUrlWithAddedQueryParameters({ path: '/bookings' }),
                          getOwaUrlWithAddedQueryParameters({ path: '/bookings' }),
                          styles.appBookings
                      ),
                  ]
                : []
        )
);

interface AppsContextMenuProps {
    onDismiss: () => any;
}

const AppsContextMenu = observer((props: AppsContextMenuProps) => (
    <div className={styles.appsContainer}>
        {Apps.get().map((appTile: AppTile) => {
            const tileClasses = classNames(styles.app, appTile.hoverBackground);

            const ariaProps: AriaProperties = {
                role: AriaRoles.menuitem,
                label: appTile.displayName,
            };

            return (
                <a
                    data-is-focusable={true}
                    href={appTile.href}
                    target={'_blank'}
                    className={tileClasses}
                    onClick={props.onDismiss}
                    {...generateDomPropertiesForAria(ariaProps)}>
                    <Icon className={styles.icon} iconName={appTile.icon} />
                    <div className={styles.text}>{appTile.displayName}</div>
                </a>
            );
        })}
    </div>
));

@observer
export default class WaffleCharm extends React.Component<HeaderPaneProps, {}> {
    render() {
        return (
            <Panel
                className={styles.appPane}
                isLightDismiss={true}
                isOpen={getActiveCharm() === HeaderCharmType.WaffleCharm}
                hasCloseButton={false}
                onDismiss={this.props.onDismiss}
                type={PanelType.smallFixedNear}>
                <AppsContextMenu onDismiss={this.props.onDismiss} />
            </Panel>
        );
    }
}
